/**
 * Search input with icon. `onChange` reports every keystroke (D-25) so the URL always holds the
 * term; a caller that sends it to a query debounces that argument with `useDebouncedQuery`.
 */

import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  testId?: string;
  /** Set when a visible `<label htmlFor>` sits above the box, so the label points somewhere. */
  id?: string;
  /** Quiet period before `onChange` fires; default `0` reports every keystroke. */
  debounceMs?: number;
}

export function SearchBar({
  value,
  onChange,
  placeholder = 'Search…',
  className = '',
  testId,
  id,
  debounceMs = 0,
}: SearchBarProps) {
  // The box renders from a local draft so typing stays responsive while `onChange` waits.
  const [draft, setDraft] = useState(value);

  // What this box last handed to `onChange`. The URL echoes that value back as a new `value`
  // a moment later, and without this the echo would be indistinguishable from a real outside
  // change — so typing on through the debounce (type, pause, type) would see the echo
  // overwrite the draft, drop the newest characters and cancel their pending report.
  const lastReportedRef = useRef(value);

  // A `value` this box did not produce is the URL's own answer — Back/Forward, Clear Filters,
  // or another control writing the same key — and wins over whatever the draft holds.
  //
  // Accepting one records it too. Without that, the ref keeps naming some older value, and a
  // later change back to it reads as this box's echo and is skipped: the draft keeps a term
  // the URL no longer has and re-reports it a moment later, restoring a filter the operator
  // just cleared.
  useEffect(() => {
    if (value !== lastReportedRef.current) {
      lastReportedRef.current = value;
      setDraft(value);
    }
  }, [value]);

  // Read through a ref: callers pass an inline arrow, so a new `onChange` identity arrives on
  // every render and would otherwise restart the timer before it could ever fire.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    // Already reported (including the `value` sync above) — nothing to schedule.
    if (draft === value) return;

    const timer = setTimeout(() => {
      lastReportedRef.current = draft;
      onChangeRef.current(draft);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [draft, value, debounceMs]);

  return (
    <div className={`relative ${className}`}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
        aria-hidden="true"
      />
      <input
        id={id}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        // `aria-label` outranks an associated `<label>` in the accessible-name computation, so
        // a box that has a visible label — which is what `id` is here for — must not set one,
        // or the spoken name and the printed one disagree.
        aria-label={id ? undefined : placeholder}
        data-testid={testId}
        className="field-box h-9 pr-4 pl-10 placeholder:text-gray-400"
      />
    </div>
  );
}
